// Engångsflytt av gamla lokala nycklar in i lagringslagret.
// Kör en gång per webbläsare så att ingen förlorar ett pågående utkast
// när de gamla mekanismerna tas bort.
import { saveDraft } from "./draftStore";
import type { DraftScope } from "./keys";

const DONE_FLAG = "bams.draft.migrated.v1";

interface LegacyMatch {
  kind: string;
  scope: DraftScope;
  data: unknown;
  savedAt?: string;
}

const parse = (raw: string | null): any => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const fromKey = (key: string): LegacyMatch | null => {
  const value = parse(localStorage.getItem(key));
  if (!value) return null;

  // skr_draft_<projekt>_<mapp> och skr_backup_<typ>_<projekt>_<mapp>_<ts>
  if (key.startsWith("skr_draft_") || key.startsWith("skr_backup_")) {
    const payload = value.data ?? value;
    if (!payload) return null;
    return {
      kind: "skr",
      scope: { kind: "skr", projectId: value.projektId ?? null, folderId: value.folderId ?? null },
      data: payload,
      savedAt: value.timestamp,
    };
  }

  // riskbedomning (global nyckel) och riskbedomning_backup_<projekt>_<mapp>_<ts>
  if (key === "riskbedomning" || key.startsWith("riskbedomning_backup_") || key.startsWith("riskbedomning_emergency_")) {
    const payload = value.data ?? value;
    if (!payload) return null;
    return {
      kind: "riskbedomning",
      scope: {
        kind: "riskbedomning",
        projectId: value.projektId ?? payload?.projektId ?? null,
        folderId: value.folderId ?? null,
      },
      data: payload,
      savedAt: value.timestamp ?? payload?._lastSavedLocal,
    };
  }

  return null;
};

/** Flyttar gamla utkast och raderar de gamla nycklarna. Idempotent. */
export const migrateLegacyDrafts = (): number => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return 0;
    if (localStorage.getItem(DONE_FLAG)) return 0;

    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }

    let moved = 0;
    const removable: string[] = [];

    keys.forEach((key) => {
      const match = fromKey(key);
      if (!match) return;
      // Bara det senaste utkastet per dokument behålls.
      const ok = saveDraft(match.scope, match.data, { unsynced: true });
      if (ok) moved += 1;
      removable.push(key);
    });

    // Gamla nycklar rensas oavsett – de har nu en ny hemvist eller var oläsbara.
    keys
      .filter(
        (key) =>
          removable.includes(key) ||
          key.startsWith("riskbedomning_backup_") ||
          key.startsWith("skr_backup_") ||
          key.startsWith("data_backup_") ||
          key.startsWith("skr_offline_queue_")
      )
      .forEach((key) => localStorage.removeItem(key));

    localStorage.setItem(DONE_FLAG, new Date().toISOString());
    return moved;
  } catch {
    return 0;
  }
};
